import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import {
  authorizePersonalSource,
  getPersonalSources,
  type PersonalSource,
  withdrawPersonalSource,
} from "@/api/client/personal-sources"
import {
  PERSONAL_SOURCE_CALLBACK_PATH,
  PERSONAL_SOURCE_RETURN_KEY,
  PERSONAL_SOURCE_SETTINGS_PATH,
} from "@/features/auth/utils/personal-source-callback"

import {
  PERSONAL_SOURCES_QUERY_KEY,
  safeMicrosoftAuthUrl,
} from "../components/personal-sources-utils"

/** The caller's own personal-source state. `available: false` when the feature is not wired. */
export function usePersonalSources() {
  return useQuery({
    queryKey: PERSONAL_SOURCES_QUERY_KEY,
    queryFn: getPersonalSources,
    retry: false,
    staleTime: 30_000,
  })
}

/** Start consent for one source: one Entra app, one scope, one sign-in. */
export function useConnectPersonalSource() {
  return useMutation({
    mutationFn: async (source: PersonalSource) => {
      const result = await authorizePersonalSource(
        source,
        `${window.location.origin}${PERSONAL_SOURCE_CALLBACK_PATH}`
      )
      const target = safeMicrosoftAuthUrl(result.auth_url)
      if (!target) throw new Error("Invalid authorization URL")
      // Consent always returns to the page that shows its result.
      sessionStorage.setItem(PERSONAL_SOURCE_RETURN_KEY, PERSONAL_SOURCE_SETTINGS_PATH)
      window.location.assign(target.href)
    },
  })
}

/** Withdraw one source: hidden at once, deleted after the grace period. */
export function useWithdrawPersonalSource() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (source: PersonalSource) => withdrawPersonalSource(source),
    onSuccess: () => client.invalidateQueries({ queryKey: PERSONAL_SOURCES_QUERY_KEY }),
  })
}
